<template>
    <div class="modals" v-if="active">
        <form-modal v-model="modals.editNote" size="lg"
            :title="$t('feedbacks.notes')"
            :loading="noteForm.loading"
            :scroll-reset="false"
            :animationDuration="0"
            :centered="false"
        >
            <new-existing-tabs>
                <template v-slot:new>
                    <note-form v-model="noteForm.data"
                        :errors="noteForm.errors"
                        :modal="modals.editNote"
                        :registration="registration"
                        :session="session"
                        :timeslot="timeslot"
                        :competencies="competenciesSelect"
                    >
                    </note-form>
                </template>
                <template v-slot:existing>
                    <feedbacks ref="notes"
                        type="note"
                        :modal="modals.editNote"
                        :registration="registration"
                        :simulation="simulation"
                        :session="session"
                        :timeslot="timeslot"
                        :competencies="competencies"
                        @update="updateNote"
                        @remove="removeNote"
                    >
                    </feedbacks>
                </template>
            </new-existing-tabs>
        </form-modal>

        <form-modal v-model="modals.editPictureFile" size="lg"
            :title="$t('feedbacks.pictures')"
            :loading="pictureForm.loading"
            :scroll-reset="false"
            :animationDuration="0"
            :centered="false"
        >
            <new-existing-tabs>
                <template v-slot:new>
                    <picture-file-form v-model="pictureForm.data"
                        :errors="pictureForm.errors"
                        :modal="modals.editPictureFile"
                        :registration="registration"
                        :session="session"
                        :timeslot="timeslot"
                        :competencies="competenciesSelect"
                        :endpoint="feedbackEndpoint"
                        @change="refresh"
                    >
                    </picture-file-form>
                </template>
                <template v-slot:existing>
                    <feedbacks ref="pictures"
                        type="picture"
                        :modal="modals.editPictureFile"
                        :registration="registration"
                        :simulation="simulation"
                        :session="session"
                        :timeslot="timeslot"
                        :competencies="competencies"
                        @update="updatePicture"
                        @remove="removePicture"
                    >
                    </feedbacks>
                </template>
            </new-existing-tabs>
        </form-modal>

        <form-modal v-model="modals.editIssue" size="lg"
            :title="$t('common.issues')"
            :loading="issueForm.loading"
            :scroll-reset="false"
            :animationDuration="0"
            :centered="false"
        >
            <new-existing-tabs>
                <template v-slot:new>
                    <issue-form v-model="issueForm.data"
                        :errors="issueForm.errors"
                        :modal="modals.editIssue"
                        :timeslot="timeslot"
                    >
                    </issue-form>
                </template>
                <template v-slot:existing>
                    <issues ref="issues"
                        :modal="modals.editIssue"
                        :registration="registration"
                        :simulation="simulation"
                        :session="session"
                        :timeslot="timeslot"
                        @update="updateIssue"
                        @remove="removeIssue"
                    >
                    </issues>
                </template>
            </new-existing-tabs>
        </form-modal>

        <form-modal v-model="modals.updateNote"
            :title="$t('feedbacks.note')"
            :loading="noteFormForUpdate.loading"
            size="lg"
            :centered="false"
        >
            <note-form v-model="noteFormForUpdate.data"
                :errors="noteFormForUpdate.errors"
                :modal="modals.updateNote"
                :registration="registration"
                :session="session"
                :timeslot="timeslot"
                :competencies="competenciesSelect"
            >
            </note-form>
        </form-modal>

        <form-modal v-model="modals.updatePicture"
            :title="$t('feedbacks.picture')"
            :loading="pictureFormForUpdate.loading"
            size="lg"
            :centered="false"
        >
            <picture-form v-model="pictureFormForUpdate.data"
                :errors="pictureFormForUpdate.errors"
                :modal="modals.updatePicture"
                :registration="registration"
                :session="session"
                :competencies="competenciesSelect"
            >
            </picture-form>
        </form-modal>

        <form-modal v-model="modals.updateIssue"
            :title="$t('common.issue')"
            :loading="issueFormForUpdate.loading"
            size="lg"
            :centered="false"
        >
            <issue-form v-model="issueFormForUpdate.data"
                :errors="issueFormForUpdate.errors"
                :modal="modals.updateIssue"
                :timeslot="timeslot"
            >
            </issue-form>
        </form-modal>

        <confirm-modal v-model="modals.confirmDeleteIssue">
            <p>{{ $t('issues.delete-confirm') }}</p>
        </confirm-modal>

        <confirm-modal v-model="modals.confirmDeleteFeedback">
            <p>{{ $t('feedbacks.delete-confirm') }}</p>
        </confirm-modal>

    </div>
</template>

<script>
    import CrudForm from '@trax/classes/CrudForm'
    import CrudClient from '@trax/classes/CrudClient'
    import NewExistingTabs from './NewExistingTabs'
    import NoteForm from '../common/NoteForm'
    import PictureForm from '../common/PictureForm'
    import PictureFileForm from '../common/PictureFileForm'
    import IssueForm from '../common/IssueForm'
    import Feedbacks from './Feedbacks'
    import Issues from './Issues'
    import moment from 'moment'

    export default {

        components: {
            NewExistingTabs, NoteForm, PictureForm, PictureFileForm, IssueForm, Feedbacks, Issues
        },

        props: {

            modals: {
                type: Object,
                required: true
            },

            registration: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            session: {
                type: Object,
                default: null
            },

            competencies: {
                type: Object,
                default: null
            },
            
            gridCompetencies: {
                type: Object,
                default: null
            },

            lang: {
                type: String,
                default: "en"
            },
        },

        data() {
            const feedbackEndpoint = this.session
                ? '/astral/api/front/dashboard/sessions/' + this.session.id + '/feedbacks'
                : '/astral/api/front/dashboard/registrations/' + this.registration.id + '/simulations/' + this.simulation.id + '/feedbacks'
            
            const issueEndpoint = this.session
                ? '/astral/api/front/dashboard/sessions/' + this.session.id + '/issues'
                : '/astral/api/front/dashboard/registrations/' + this.registration.id + '/simulations/' + this.simulation.id + '/issues'

            let feedbackClient = new CrudClient(feedbackEndpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
            })
            let issueClient = new CrudClient(issueEndpoint, {
                registration_id: this.registration.id,
                simulation_id: this.simulation.id,
                session_id: this.session ? this.session.id : null,
            })
            return {
                feedbackEndpoint,
                issueEndpoint,
                feedbackClient,
                issueClient,
                noteForm: new CrudForm(feedbackClient, this.mapNote, this.unmapNote),
                pictureForm: new CrudForm(feedbackClient, this.mapPicture, this.unmapPicture),
                issueForm: new CrudForm(issueClient, this.mapIssue, this.unmapIssue),
                noteFormForUpdate: new CrudForm(feedbackClient, this.mapNote, this.unmapNote),
                pictureFormForUpdate: new CrudForm(feedbackClient, this.mapPicture, this.unmapPicture),
                issueFormForUpdate: new CrudForm(issueClient, this.mapIssue, this.unmapIssue),
                timeslot: null,
                moment: moment,
            }
        },

        created() {
            this.noteForm.init(this, 'noteForm')
            this.noteFormForUpdate.init(this, 'noteFormForUpdate')
            this.pictureForm.init(this, 'pictureForm')
            this.pictureFormForUpdate.init(this, 'pictureFormForUpdate')
            this.issueForm.init(this, 'issueForm')
            this.issueFormForUpdate.init(this, 'issueFormForUpdate')
        },

        computed: {

            active() {
                return this.$permissions.hasRight('dashboard.pilot-log.access')
                    || (this.session && this.session.started_at && !this.session.closed_at)
            },

            competenciesSelect() {
                let select = {}
                if (!this.gridCompetencies) {
                    return select
                }
                for (let id in this.gridCompetencies) {
                    select[id] = this.gridCompetencies[id]['name_'+this.lang]
                }
                return select
            },
        },

        methods: {
            
            refresh() {
                this.$emit('refresh')
            },

            createNote(timeslot) {
                this.timeslot = timeslot
                this.noteForm.mapData = this.mapNote
                this.noteForm.unmapData = this.unmapNote

                // Do this at the main popup opening.
                this.noteFormForUpdate.mapData = this.mapNote
                this.noteFormForUpdate.unmapData = this.unmapNote

                this.modals.open('editNote')
                this.noteForm.create().then(resp => {
                    this.modals.close('editNote')
                    this.refresh()
                })
            },

            createPicture(timeslot) {
                this.timeslot = timeslot
                this.pictureForm.mapData = this.mapPicture
                this.pictureForm.unmapData = this.unmapPicture

                // Do this at the main popup opening.
                this.pictureFormForUpdate.mapData = this.mapPicture
                this.pictureFormForUpdate.unmapData = this.unmapPicture

                this.modals.open('editPictureFile')
                this.pictureForm.create().then(resp => {
                    this.modals.close('editPictureFile')
                    this.refresh()
                })
            },

            createIssue(timeslot) {
                this.timeslot = timeslot
                this.issueForm.mapData = this.mapIssue
                this.issueForm.unmapData = this.unmapIssue

                // Do this at the main popup opening.
                this.issueFormForUpdate.mapData = this.mapIssue
                this.issueFormForUpdate.unmapData = this.unmapIssue

                this.modals.open('editIssue')
                this.issueForm.create().then(resp => {
                    this.modals.close('editIssue')
                    this.refresh()
                })
            },

            updateNote(feedback) {
                this.modals.open('updateNote')
                this.noteFormForUpdate.update(feedback).then(resp => {
                    this.modals.close('updateNote')
                    this.resetModal('editNote')
                    this.$refs.notes.list()
                })
            },

            updatePicture(feedback) {
                this.modals.open('updatePicture')
                this.pictureFormForUpdate.update(feedback).then(resp => {
                    this.modals.close('updatePicture')
                    this.resetModal('editPictureFile')
                    this.$refs.pictures.list()
                })
            },

            updateIssue(issue) {
                this.modals.open('updateIssue')
                this.issueFormForUpdate.update(issue).then(resp => {
                    this.modals.close('updateIssue')
                    this.resetModal('editIssue')
                    this.$refs.issues.list()
                })
            },

            removeNote(feedback) {
                this.modals.open('confirmDeleteFeedback').then(resp => {
                    if (resp.confirmed) {
                        this.resetModal('editNote')
                        this.feedbackClient.delete(feedback.id).then(resp => {
                            this.$refs.notes.list()
                            this.refresh()
                        })
                    }
                })
            },

            removePicture(feedback) {
                this.modals.open('confirmDeleteFeedback').then(resp => {
                    if (resp.confirmed) {
                        this.resetModal('editPictureFile')
                        this.feedbackClient.delete(feedback.id).then(resp => {
                            this.$refs.pictures.list()
                            this.refresh()
                        })
                    }
                })
            },

            removeIssue(issue) {
                this.modals.open('confirmDeleteIssue').then(resp => {
                    if (resp.confirmed) {
                        this.resetModal('editIssue')
                        this.issueClient.delete(issue.id).then(resp => {
                            this.$refs.issues.list()
                            this.refresh()
                        })
                    }
                })
            },

            resetModal(name) {
                this.modals.close(name)
                this.$nextTick(() => {
                    this.modals.open(name)
                });
            },

            mapPicture(feedback) {
                let data = this.mapFeedback(feedback)
                // We need to add this now because data will be directly sent by the form,
                // without passing by the unmapFeedback method.
                data.type = 'picture'
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.timeslot_id = this.timeslot ? this.timeslot.id : null
                data.learner1_id = this.session ? this.session.learner1_id : null
                data.learner2_id = this.session ? this.session.learner2_id : null
                return data
            },

            mapNote(feedback) {
                return this.mapFeedback(feedback)
            },

            mapFeedback(feedback) {
                return {
                    id: feedback ? feedback.id : null,
                    log_time_from: feedback ? feedback.log_time_from : null,
                    log_time_to: feedback ? feedback.log_time_to : null,
                    learner_summary: feedback ? feedback.learner_summary : null,
                    public_description: feedback ? feedback.public_description : null,
                    confidential_description: feedback ? feedback.confidential_description : null,
                    competency_ids: feedback ? (feedback.competency_ids
                        // Competency_ids may already be defined when coming from PWA (not from the back-end).
                        ? feedback.competency_ids
                        : feedback.competencies.map(competency => competency.id.toString())
                    ) : null,
                    personal: feedback ? feedback.personal == 1 || feedback.personal == "true" : true,
                    learner1_check: feedback ? feedback.learner1_id != null : false,
                    learner2_check: feedback ? feedback.learner2_id != null : false,
                }
            },

            mapIssue(issue) {
                return {
                    id: issue ? issue.id : null,
                    log_time_from: issue ? issue.log_time_from : null,
                    log_time_to: issue ? issue.log_time_to : null,
                    entered_at: issue ? issue.entered_at : null,
                    summary: issue ? issue.summary : null,
                    description: issue ? issue.description : null,
                }
            },

            unmapNote(data) {
                return this.unmapFeedback(data, 'note')
            },

            unmapPicture(data) {
                return this.unmapFeedback(data, 'picture')
            },

            unmapFeedback(data, type) {
                data.type = type
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.timeslot_id = this.timeslot ? this.timeslot.id : null
                data.learner1_id = data.learner1_check && this.session ? this.session.learner1_id : null
                data.learner2_id = data.learner2_check && this.session ? this.session.learner2_id : null
                return data
            },

            unmapIssue(data) {
                data.registration_id = this.registration.id
                data.simulation_id = this.simulation.id
                data.session_id = this.session ? this.session.id : null
                data.timeslot_id = this.timeslot ? this.timeslot.id : null
                data.entered_at = this.moment().format('YYYY-MM-DD HH:mm:ss')
                data.author_role = this.$permissions.hasRight('dashboard.pilot-log.access')
                    ? 'pilot'
                    : 'instructor'
                return data
            },
        }
    }
</script>
